import React, { useState, useEffect } from 'react';
import { Modal, Empty, Typography, Spin, App } from 'antd';

import { imagenService } from '../../../../services/api';
import FotosAnimal, { conImagenActualizada } from '../../../../components/FotosAnimal';

const { Text } = Typography;

/**
 * Las fotos de UN animal con sus controles de presentación.
 *
 * Es el mismo juego de controles que la tabla de "Fotos y videos" y que la card
 * «Fotos» de la ficha del animal, acotado a un animal. La lista de filas es
 * `FotosAnimal`, la misma que usa la ficha, y se importa en vez de copiarla: el
 * día que cambie cómo se marca una portada tiene que cambiar en todos los
 * lugares o el productor aprende dos comportamientos para lo mismo. Acá SIN
 * tachito: este modal es de presentación; borrar se hace desde la ficha.
 *
 * PIDE LAS FOTOS AL ABRIRSE y no usa las que trae la fila de la tabla. Las de
 * la fila vienen del listado del catálogo, que se carga una vez: si el
 * productor marca una portada acá, cierra y vuelve a abrir, vería el estado
 * viejo. Una consulta por apertura de modal es barata y siempre dice la verdad.
 */
const ModalPresentacionFotos = ({ animal, abierto, onCerrar, onCambio }) => {
  // `App.useApp()` y no el `message` estatico: el estatico no toma el tema ni
  // el contexto del <App>, y el resto de esta pantalla ya lo usa asi.
  const { message } = App.useApp();
  const [imagenes, setImagenes] = useState([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!abierto || !animal?.id_animal) return undefined;
    let vivo = true;
    setCargando(true);
    (async () => {
      try {
        const res = await imagenService.getByAnimal(animal.id_animal);
        // `GET /imagenes/animal/:id` devuelve SIEMPRE `{ imagenes, total }`
        // (imagen_routes.py). Aca decia que "a veces devuelve la lista pelada"
        // y aceptaba las dos formas: era falso, y un comentario que inventa
        // una forma del backend es el mismo pozo que la ficha ya documenta
        // para este mismo endpoint. Misma lectura que `AnimalForm`.
        if (vivo) setImagenes(res.data?.imagenes || []);
      } catch (err) {
        if (vivo) {
          setImagenes([]);
          message.error(err.response?.data?.error || 'No pudimos traer las fotos');
        }
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
    // `message` viene de `App.useApp()` y es estable entre renders, pero va en
    // las dependencias igual: dejarlo afuera obliga a confiar en un detalle de
    // antd que puede cambiar, y el lint tiene razon en pedirlo.
  }, [abierto, animal?.id_animal, message]);

  const alCambiar = (actualizada) => {
    // La regla de "marcar una portada desmarca la anterior" vive en
    // `conImagenActualizada`, junto a la lista que la muestra.
    const siguiente = conImagenActualizada(imagenes, actualizada);
    setImagenes(siguiente);
    // HACIA AFUERA VIAJA LA LISTA ENTERA, no un aviso pelado. El que llama
    // necesita PARCHEAR sus filas en memoria y no recargar: en esta pantalla
    // recargar devuelve la lista al orden del servidor y se come un reorden sin
    // guardar. El archivo ya documenta ese mismo pozo en `guardarNombreCorto`.
    if (onCambio) onCambio(siguiente);
  };

  const nombre = animal?.nombre_corto || animal?.nom_animal || 'este animal';

  return (
    <Modal
      open={abierto}
      onCancel={onCerrar}
      title={`Fotos de ${nombre}`}
      footer={null}
      width={620}
      // `destroyOnClose` para que el efecto vuelva a correr en la próxima
      // apertura aunque sea el mismo animal: es lo que garantiza que no se vea
      // un estado viejo.
      destroyOnClose
    >
      {cargando ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : imagenes.length === 0 ? (
        <Empty
          description={
            <>
              <div>Este animal todavía no tiene fotos cargadas.</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Se suben desde la ficha del animal, o en tanda desde Animales → Fotos y videos.
              </Text>
            </>
          }
          style={{ padding: '32px 0' }}
        />
      ) : (
        <FotosAnimal imagenes={imagenes} onCambio={alCambiar} />
      )}
    </Modal>
  );
};

export default ModalPresentacionFotos;
